import { Box, Typography } from '@mui/material';
import { LibraryGalleryLoadMore } from './LibraryGalleryLoadMore';

export interface LibraryGalleryFooterProps {
  itemCount: number;
  totalCount: number;
  /** Noun for the count line — "blocks" / "templates". */
  noun: string;
  /** Singular of `noun`, used when the total is exactly one. Defaults to `noun` without a trailing "s". */
  singularNoun?: string;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoadingMore?: boolean;
  /**
   * The grid still shows the previous result while a new filter or refetch is in flight, so `hasMore`
   * describes stale data: "Load more" stays in place (focus is not lost) but is disabled until the
   * current result arrives.
   */
  isRefreshing?: boolean;
  /** Fetching the next page failed while earlier pages are still shown: an inline alert + "Try again" replaces "Load more". */
  loadMoreError?: string | null;
  testIdPrefix: string;
}

/**
 * The gallery's "Load more" step — a card gallery is browsed continuously, so it takes
 * Load more rather than a pagination bar. The count line is `aria-live="polite"` so screen
 * reader users hear it update; the button carries `aria-busy` (via `RwButton`'s `loading`)
 * while more items are in flight. A failed next-page fetch is shown by `LibraryGalleryLoadMore`
 * as an inline alert with "Try again", so the pages already loaded stay on screen.
 */
export function LibraryGalleryFooter({
  itemCount,
  totalCount,
  noun,
  singularNoun,
  hasMore,
  onLoadMore,
  isLoadingMore = false,
  isRefreshing = false,
  loadMoreError = null,
  testIdPrefix
}: LibraryGalleryFooterProps) {
  return (
    <Box
      sx={{
        mt: 'var(--size-24)',
        pt: 'var(--size-16)',
        borderTop: 'var(--border-width-xs) solid var(--border-neutral-subtle)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--size-12)'
      }}
    >
      <Typography aria-live="polite" variant="body2" data-testid={`${testIdPrefix}:count`} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        Showing{' '}
        <Box component="strong" sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
          {itemCount}
        </Box>{' '}
        of{' '}
        <Box component="strong" sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
          {totalCount}
        </Box>{' '}
        {totalCount === 1 ? (singularNoun ?? noun.replace(/s$/, '')) : noun}
      </Typography>
      <LibraryGalleryLoadMore
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        isRefreshing={isRefreshing}
        loadMoreError={loadMoreError}
        testIdPrefix={testIdPrefix}
      />
    </Box>
  );
}
